/**
 * L'UN Single Promise — cart is forced to exactly one product.
 * Adding a new product clears the cart first so the new item always replaces the old one.
 * The shopper is told about it in an on-brand dialog before the cart is replaced.
 *
 * Internal cart reads and writes use the original fetch: going through the wrapper made every
 * /cart.js read schedule another check, a loop of ~10 requests per second on every page.
 */
(function () {
  // Copy comes from the theme settings through data attributes on this script tag (see layout/theme.liquid).
  const data = (document.currentScript && document.currentScript.dataset) || {};
  const TEXT = {
    title: data.title || 'One promise at a time',
    message: data.message || "You have only one single promise at L'UN.",
    button: data.button || 'Got it',
  };
  const originalFetch = window.fetch.bind(window);

  async function getCart() {
    try {
      const res = await originalFetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return { item_count: 0, items: [] };
      return await res.json();
    } catch {
      return { item_count: 0, items: [] };
    }
  }

  async function clearCart() {
    try {
      await originalFetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      });
    } catch (e) {
      console.warn('[lun] clear failed', e);
    }
  }

  /** Keeps the header count honest after a silent correction. */
  function renderCount(count) {
    const icon = document.querySelector('cart-icon');
    if (icon && typeof icon.renderCartBubble === 'function') icon.renderCartBubble(count, false);
  }

  let forcing = null;

  function forceSingleItem() {
    // one check at a time; callers arriving meanwhile share it
    if (!forcing) {
      forcing = (async () => {
        const cart = await getCart();
        if (!cart.items || cart.items.length === 0) return;
        if (cart.items.length === 1 && cart.items[0].quantity === 1) return;

        const last = cart.items[cart.items.length - 1];
        const updates = {};
        cart.items.forEach((item) => {
          updates[item.key] = item.key === last.key ? 1 : 0;
        });

        try {
          await originalFetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates }),
            credentials: 'same-origin',
          });
          renderCount(1);
        } catch (e) {
          console.warn('[lun] forceSingle failed', e);
        }
      })().finally(() => {
        forcing = null;
      });
    }
    return forcing;
  }

  const DIALOG_CSS = `
    .lun-promise {
      inline-size: min(26rem, calc(100vw - 2rem));
      max-inline-size: none;
      padding: 0;
      border: 0;
      background: transparent;
      color: var(--lun-ink, #3b1f27);
      overflow: visible;
    }

    .lun-promise::backdrop {
      background: rgb(59 31 39 / 0.45);
      backdrop-filter: blur(4px);
    }

    .lun-promise[open] {
      animation: lun-promise-in 0.35s cubic-bezier(0.2, 0.7, 0.2, 1);
    }

    .lun-promise__card {
      display: grid;
      justify-items: center;
      gap: 0.9rem;
      padding: 2.5rem 2rem 2rem;
      border: 1px solid var(--lun-hair, rgb(90 52 62 / 0.18));
      border-radius: 1.75rem;
      background: var(--lun-paper, #fbf6f0);
      box-shadow: var(--lun-shadow, 0 1.25rem 3rem rgb(90 52 62 / 0.16));
      text-align: center;
    }

    .lun-promise__knot {
      inline-size: 3.25rem;
      block-size: 3.25rem;
      border-radius: 50%;
      background: var(--lun-blush, #f9dce2)
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23D95F7F' d='M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z'/%3E%3C/svg%3E")
        center / 1.5rem no-repeat;
    }

    .lun-promise__title {
      margin: 0;
      font-family: var(--font-heading--family, 'Libre Baskerville', Georgia, serif);
      font-size: 1.6rem;
      font-weight: 400;
      line-height: 1.2;
    }

    .lun-promise__text {
      margin: 0 0 0.9rem;
      font-family: var(--font-body--family, Georgia, serif);
      font-size: 1rem;
      line-height: 1.6;
      color: var(--lun-ink-soft, rgb(59 31 39 / 0.72));
    }

    @keyframes lun-promise-in {
      from { opacity: 0; transform: translateY(0.75rem) scale(0.98); }
      to { opacity: 1; transform: none; }
    }

    @media (prefers-reduced-motion: reduce) {
      .lun-promise[open] { animation: none; }
    }
  `;

  function injectDialogStyles() {
    if (document.getElementById('lun-promise-styles')) return;
    const style = document.createElement('style');
    style.id = 'lun-promise-styles';
    style.textContent = DIALOG_CSS;
    document.head.appendChild(style);
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text; // textContent: theme settings are never parsed as HTML
    return node;
  }

  /**
   * Shows the on-brand notice and resolves once the shopper dismisses it
   * (button, Esc or a click outside), so the replacement only happens afterwards.
   */
  function showMessage() {
    if (typeof HTMLDialogElement !== 'function') {
      alert(TEXT.message);
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      injectDialogStyles();

      const dialog = element('dialog', 'lun-promise');
      dialog.setAttribute('aria-labelledby', 'lun-promise-title');
      dialog.setAttribute('aria-describedby', 'lun-promise-text');

      const card = element('div', 'lun-promise__card');
      const title = element('h2', 'lun-promise__title', TEXT.title);
      title.id = 'lun-promise-title';
      const text = element('p', 'lun-promise__text', TEXT.message);
      text.id = 'lun-promise-text';
      const button = element('button', 'button lun-cta', TEXT.button);
      button.type = 'button';
      card.append(element('span', 'lun-promise__knot'), title, text, button);
      dialog.append(card);

      button.addEventListener('click', () => dialog.close());
      // a click on the backdrop lands on the <dialog> itself, not on the card
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => {
        dialog.remove();
        resolve();
      });

      document.body.appendChild(dialog);
      dialog.showModal();
    });
  }

  /** A comparable key for an add request body (same variant twice = same key). */
  function addKey(init) {
    const body = init && init.body;
    if (body instanceof FormData) return `id:${body.get('id')}`;
    if (typeof body === 'string') {
      try {
        const json = JSON.parse(body);
        const first = (json.items && json.items[0]) || json;
        if (first && first.id) return `id:${first.id}`;
      } catch {
        return body;
      }
      return body;
    }
    return null;
  }

  /** @type {{ key: string | null, promise: Promise<Response> } | null} */
  let addInFlight = null;

  async function singleAdd(input, init) {
    const cart = await getCart();
    if (cart.item_count >= 1) {
      await showMessage();
      await clearCart();
      await new Promise((r) => setTimeout(r, 80));
    }
    return originalFetch(input, init);
  }

  // Intercept every cart/add fetch and clear first if cart already has an item
  window.fetch = async function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const isAdd = /\/cart\/add/.test(url);

    if (!isAdd) {
      const response = await originalFetch(input, init);
      // only cart writes can break the rule; reads must not trigger another check
      if (/\/cart\/(change|update)/.test(url)) setTimeout(forceSingleItem, 100);
      return response;
    }

    const key = addKey(init);

    // A double tap sends the same add twice while the first is still running: share its response
    if (addInFlight && key !== null && addInFlight.key === key) {
      return (await addInFlight.promise).clone();
    }

    // A different add while one is running waits for it, then replaces it as usual
    const previous = addInFlight ? addInFlight.promise.catch(() => {}) : Promise.resolve();
    const promise = previous.then(() => singleAdd(input, init));
    const entry = { key, promise };
    addInFlight = entry;

    try {
      const response = await promise;
      setTimeout(forceSingleItem, 100);
      return response.clone();
    } finally {
      if (addInFlight === entry) addInFlight = null;
    }
  };

  // Safety net for adds that bypass fetch (native form posts reload the page; apps may use XHR)
  document.addEventListener('DOMContentLoaded', forceSingleItem);
  window.addEventListener('pageshow', forceSingleItem);
  setInterval(() => {
    if (document.visibilityState === 'visible') forceSingleItem();
  }, 2000);
})();
